import { useEffect, useState } from 'react'

import { useTranslations } from 'next-intl'

import classnames from 'classnames'

import AnimateIntoView, {
  AnimationType,
} from '~/components/atoms/AnimateIntoView/AnimateIntoView'
import Arrow from '~/components/atoms/Arrow/Arrow'
import Button from '~/components/atoms/Button/Button'
import type { ImageProps } from '~/components/atoms/Image/Image'
import Image from '~/components/atoms/Image/Image'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import VerticalPadder from '~/components/atoms/VerticalPadder/VerticalPadder'
import type { VimeoInput } from '~/components/atoms/VideoPlayback/VideoPlayback'
import VideoPlayback from '~/components/atoms/VideoPlayback/VideoPlayback'
import type { LinkProps } from '~/components/links'
import { resolveLink } from '~/components/serializers'
import useFontFaceObserver from '~/hooks/useFontFaceObserver'
import useFormAnalytics from '~/hooks/useFormAnalytics'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'

import styles from './SliderHero.module.css'

export interface Slide {
  background: string
  title: string
  tabTitle: string
  summary: string
  link: LinkProps
  image?: ImageProps
  video?: VimeoInput
  launchForm?: string
}

export interface SliderHeroProps {
  slides: Slide[]
  testID?: string
}

export default function SliderHero({ slides, testID }: SliderHeroProps) {
  const t = useTranslations('sliderHero')
  const [slideNo, setSlideNo] = useState<number>(0)
  const [showIntroBanner, setShowIntroBanner] = useState(false)
  const [introBannerComplete, setIntroBannerComplete] = useState(false)
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMedium})`)
  const { handleFormLaunchFromCtaAnalytics } = useFormAnalytics()

  const isFontListLoaded = useFontFaceObserver([
    {
      family: `Noir No1`,
    },
    {
      family: `Canela Web`,
    },
  ])

  const nextSlide = () => {
    if (slideNo === slides.length - 1) {
      setSlideNo(0)
    } else {
      setSlideNo(slideNo + 1)
    }
  }

  const prevSlide = () => {
    if (slideNo === 0) {
      setSlideNo(slides.length - 1)
    } else {
      setSlideNo(slideNo - 1)
    }
  }

  useEffect(() => {
    if (introBannerComplete) {
      const timeout = setTimeout(nextSlide, 7000)
      return () => clearTimeout(timeout)
    }
  }, [slideNo, introBannerComplete])

  useEffect(() => {
    const hasVisited = localStorage.getItem('hasVisited')

    if (hasVisited === null) {
      setShowIntroBanner(true)
    } else {
      const expiry = JSON.parse(hasVisited)?.expiry
      if (new Date() > expiry) {
        handleLocalStorage()
        setShowIntroBanner(true)
      } else {
        setShowIntroBanner(false)
        setIntroBannerComplete(true)
      }
    }
  }, [])

  useEffect(() => {
    // handle the fadeout/hiding of the animated intro banner
    if (!introBannerComplete && showIntroBanner) {
      const timeout = setTimeout(() => {
        setIntroBannerComplete(true)
        handleLocalStorage()
      }, 5000)
      return () => clearTimeout(timeout)
    }
  }, [isFontListLoaded, showIntroBanner, introBannerComplete])

  const handleLocalStorage = () => {
    const hasVisited = localStorage.getItem('hasVisited')
    if (hasVisited !== null) localStorage.removeItem('hasVisited')

    const expiryDate = new Date()
    expiryDate.setMonth((expiryDate.getMonth() + 1) % 12) // set expiry for one month

    localStorage.setItem('hasVisited', JSON.stringify({ expiry: expiryDate }))
  }

  const renderAnimatedIntro = () => {
    return (
      <div
        className={classnames(
          styles.animatedIntroBanner,
          introBannerComplete && styles.animatedIntroBannerFade
        )}
      >
        <div className={styles.introBanner}>
          {isFontListLoaded && (
            <div className={styles.introBannerInner}>
              <AnimateIntoView type={AnimationType.fadeIn}>
                <Text
                  Tag="p"
                  size={TextSizes.secondaryTitleLrg}
                  className={styles.introBannerIntro}
                >
                  {t('introText')}
                </Text>
              </AnimateIntoView>
              <AnimateIntoView type={AnimationType.fadeInFast} delay={1000}>
                <Text
                  Tag="p"
                  size={
                    isMobile
                      ? TextSizes.primaryTitleLrg
                      : TextSizes.primaryTitleLrger
                  }
                >
                  {t('feature1')}
                </Text>
              </AnimateIntoView>
              <AnimateIntoView type={AnimationType.fadeInFast} delay={1500}>
                <Text
                  Tag="p"
                  size={
                    isMobile
                      ? TextSizes.primaryTitleLrg
                      : TextSizes.primaryTitleLrger
                  }
                >
                  {t('feature2')}
                </Text>
              </AnimateIntoView>
              <AnimateIntoView type={AnimationType.fadeInFast} delay={2000}>
                <Text
                  Tag="p"
                  size={
                    isMobile
                      ? TextSizes.primaryTitleLrg
                      : TextSizes.primaryTitleLrger
                  }
                >
                  {t('feature3')}
                </Text>
              </AnimateIntoView>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={classnames(styles.base)} data-testid={testID}>
      {showIntroBanner && renderAnimatedIntro()}
      <div className={styles[slides[slideNo].background]}>
        <div className={styles.inner}>
          <div className={styles.sliderMedia}>
            {slides.map((slide, i) => {
              if (slide?.tabTitle == 'MARCHÉ') return null
              return (
                <div
                  className={classnames(
                    styles.slideMediaInner,
                    slideNo === i && styles.slideMediaInnerActive
                  )}
                  key={i}
                >
                  {slide.image && (
                    <>
                      <Image
                        asset={slide.image.asset}
                        objectFit="cover"
                        alt={slide.title}
                        sizes="(max-width: 799px) 80vw, 50vw"
                        priority={true}
                      />
                      {slide.video && (
                        <VideoPlayback vimeo={slide.video} ambientLoop={true} />
                      )}
                    </>
                  )}
                  {slide.image?.credit?.text && (
                    <div
                      className={classnames(styles.imageCredit, {
                        [styles[slide.image.credit.theme]]:
                          slide.image.credit.theme,
                      })}
                    >
                      <Text Tag="p" size={TextSizes.tertiaryTitleSml}>
                        {slide.image.credit.text}
                      </Text>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          <div className={styles.sliderContent}>
            <div className={styles.slideContentInner}>
              <VerticalPadder
                noPaddingBottom={isMobile}
                noPaddingTop={isMobile}
              >
                <div className={styles.sliderNavigation}>
                  {slides.map((slide, i) => {
                    if (slide?.tabTitle == 'MARCHÉ') return null
                    return (
                      <button
                        key={i}
                        onClick={() => setSlideNo(i)}
                        className={classnames(
                          styles.tabButton,
                          slideNo === i && styles.tabButtonActive
                        )}
                      >
                        <span>{slide.tabTitle}</span>
                      </button>
                    )
                  })}
                </div>

                <div className={styles.sliderText}>
                  <div>
                    {slides.map((slide, i) => {
                      if (slide?.tabTitle == 'MARCHÉ') return null
                      return (
                        <div
                          key={i}
                          className={classnames(
                            styles.slideText,
                            slideNo === i && styles.slideTextActive
                          )}
                        >
                          <div className={styles.slideTextInner}>
                            <Text
                              size={TextSizes.primaryTitleLrger}
                              className={styles.slideTitle}
                              Tag="h2"
                            >
                              <span>{slide.title}</span>
                            </Text>
                            <Text
                              size={TextSizes.secondaryTitleLrg}
                              className={styles.slideSummary}
                              Tag="p"
                            >
                              <span>{slide.summary}</span>
                            </Text>
                          </div>
                          {slide.link && (
                            <Button
                              link={resolveLink(slide.link)}
                              launchForm={slide.launchForm}
                              gaTrack={slide.link.gaTrack}
                              gaTrackEventName={slide.link.gaTrackEventName}
                              phCTASource={slide.link?.phCTASource}
                              phEventName={slide.link?.phEventName}
                              phInteractionLocation={
                                slide.link?.phInteractionLocation
                              }
                              phWebProduct={slide.link?.phWebProduct}
                            >
                              {slide.link.text}
                            </Button>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  <div className={styles.sliderArrows}>
                    <button onClick={prevSlide}>
                      <Arrow pointLeft />
                    </button>

                    <button onClick={nextSlide}>
                      <Arrow />
                    </button>
                  </div>
                </div>
              </VerticalPadder>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
